// Canonical studio.client logger entry point. Exports the `logger`
// instance plus a redaction-wrapped variant that scrubs secrets per the
// W1 redaction contract before reaching console.log / Sentry.
//
// Per rule: use-canonical-logger-not-console (ai.knowledge/platform/
// studio.client) — every studio.client call site uses
// `import { logger } from '#~/logger/index.js'`. We preserve that import
// path (no breaking change) and add the redaction hook transparently.
//
// Per rule: stay-in-scope (ai.knowledge/global) — additive. The
// underlying logger.ts is untouched. We wrap it here at the boundary so
// every existing call site picks up redaction without per-site edits.
//
// Per rule: reference-rules-in-comments (ai.knowledge/global) — see
// tools/diagnostic-schema/SCHEMA.md §3 for the boot_id propagation
// contract this file implements on the renderer side.
//
// User story: when the next Shopify-style auto-update incident lands,
// the support engineer's first grep is `boot_id=<uuid>` to filter every
// log line from a single customer event across all processes. The
// renderer must include boot_id in its log output so Sentry events,
// browser console transcripts, and Electron's loggy file all share the
// same correlation key.

import type { ILogger, Level, Message } from '@studio/js-utils/logger';
import {
  ANALYTICS_ID_ENV_NAMES,
  COMMON_PROPERTIES
} from '#~/analytics/constants/event-properties.js';
import { logger as baseLogger } from './logger.js';
import { redact, redactArgs } from './redact.js';

// boot_id resolution.
//
// Renderer source priority (per W1 SCHEMA.md §3 propagation contract):
//   1. `window.IRIS_BOOT_ID` if Electron preload injected it. Electron
//      main has the canonical UUID (init.ts); preload can attach it via
//      contextBridge.exposeInMainWorld so the renderer sees the same
//      string that bridge child processes do.
//   2. `import.meta.env.VITE_IRIS_BOOT_ID` if a build-time injection is
//      configured (covers the studio web non-Electron deployment).
//   3. A self-generated session UUID as a last resort. Per W1, a
//      process that can't see the canonical boot_id generates its own
//      and logs `boot_id_source=self` so the support engineer sees the
//      correlation gap.
//
// We resolve at module-load time and freeze the value for the lifetime
// of the renderer instance — boot_id changing mid-session would break
// the correlation contract.
function resolveBootId(): { value: string; source: 'electron' | 'env' | 'self' } {
  // Prefer the preload bridge so renderer logs share main's boot identity.
  if (typeof window !== 'undefined') {
    const bridgedEnv = (
      window as unknown as { electron?: { process?: { env?: Record<string, string> } } }
    ).electron?.process?.env;
    const bridged = bridgedEnv?.[ANALYTICS_ID_ENV_NAMES.BOOT_ID];
    if (typeof bridged === 'string' && bridged.length > 0) {
      return { value: bridged, source: 'electron' };
    }
    // Preserve the legacy direct-window fallback.
    const w = window as unknown as Record<string, unknown>;
    const directBootId = w[ANALYTICS_ID_ENV_NAMES.BOOT_ID];
    if (typeof directBootId === 'string' && directBootId.length > 0) {
      return { value: directBootId, source: 'electron' };
    }
  }

  // Build-time injection for non-Electron deployments. Vite exposes
  // `import.meta.env.VITE_*` to the renderer.
  // We access it via a narrow indexed lookup so this file stays usable
  // under unit tests where import.meta.env may not be populated.
  try {
    const env = (import.meta as { env?: Record<string, string | undefined> }).env;
    const fromEnv = env?.VITE_IRIS_BOOT_ID;
    if (typeof fromEnv === 'string' && fromEnv.length > 0) {
      return { value: fromEnv, source: 'env' };
    }
  } catch {
    // import.meta.env not available — fall through to self-generation.
  }

  // Self-generate. crypto.randomUUID() is available in modern browsers
  // AND in Node.js (jest environment); we narrow the typeof to be safe.
  const c = typeof crypto !== 'undefined' ? (crypto as Crypto) : undefined;
  if (c && typeof c.randomUUID === 'function') {
    return { value: c.randomUUID(), source: 'self' };
  }

  // Final fallback for environments without crypto.randomUUID — produce
  // a plausible v4-ish string. This branch is essentially unreachable in
  // production (every supported browser has crypto.randomUUID), but it
  // keeps the contract that boot_id is always a string.
  return {
    value: 'unknown-boot-id-no-crypto',
    source: 'self'
  };
}

const bootIdCtx = resolveBootId();

/**
 * Returns the boot_id this renderer is correlating its logs against.
 * Used by future call sites that want to attach boot_id to non-logger
 * sinks (e.g. analytics events, IPC payloads, error boundaries).
 *
 * The value is frozen at module load — see `resolveBootId()` for the
 * source priority.
 */
export function getBootId(): string {
  return bootIdCtx.value;
}

/**
 * Returns where boot_id was sourced from: 'electron' (preload bridge),
 * 'env' (Vite build-time), or 'self' (locally generated). Per W1
 * SCHEMA.md §3 a 'self' source is a diagnostic signal that the
 * Electron-renderer correlation handshake didn't happen.
 */
export function getBootIdSource(): 'electron' | 'env' | 'self' {
  return bootIdCtx.source;
}

// Wrap the canonical logger with the redaction hook + boot_id context.
//
// Pattern: a thin proxy that calls `logger.log()` with redacted args and
// the boot_id context appended. The base logger is left intact (and
// remains the default export of './logger.js') so any code that imports
// from there directly still works.
//
// We DON'T introduce console.* calls here — every output path lands in
// the base logger which is the canonical sink (per
// use-canonical-logger-not-console.md).
class RedactedLogger implements ILogger {
  // Note: properties of the base logger are accessed via the wrapped
  // instance below. This class only intercepts the variadic-args
  // surface to inject redaction + context.

  public log(level: Level, message: Message, ...args: unknown[]): ILogger {
    const redactedMessage = typeof message === 'string' ? redactStringSafe(message) : message;
    const redactedArgs = redactArgs(args);

    // Append boot_id as a structured trailing arg. The base logger uses
    // console.log under the hood which preserves arg order, so a context
    // object at the end is the conventional way to attach metadata
    // without altering the human-readable message body.
    const ctx = {
      [COMMON_PROPERTIES.BOOT_ID]: bootIdCtx.value,
      boot_id_source: bootIdCtx.source
    };

    baseLogger.log(level, redactedMessage, ...redactedArgs, ctx);
    return this;
  }

  public critical(message: Message, ...args: unknown[]): ILogger {
    return this.log('critical', message, ...args);
  }
  public debug(message: Message, ...args: unknown[]): ILogger {
    return this.log('debug', message, ...args);
  }
  public error(message: Message, ...args: unknown[]): ILogger {
    return this.log('error', message, ...args);
  }
  public info(message: Message, ...args: unknown[]): ILogger {
    return this.log('info', message, ...args);
  }
  public warning(message: Message, ...args: unknown[]): ILogger {
    return this.log('warning', message, ...args);
  }
}

// String redaction wrapper that fail-opens on any error in the redaction
// pipeline — see comment on redactArgs() in redact.ts. A redaction bug
// must never silence a log line.
function redactStringSafe(s: string): string {
  try {
    return redact(s);
  } catch {
    return s;
  }
}

// Export the redacted logger as the canonical `logger`. Existing call
// sites (`import { logger } from '#~/logger/index.js'`) automatically
// get redaction + boot_id context with zero per-site changes.
export const logger: ILogger = new RedactedLogger();
